import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Bell, BellOff, CircleDot, Volume2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useWorkflowAttention } from "@/contexts/WorkflowAttentionContext";
import { attentionLine, displayTitle, pendingInputCount } from "@/lib/workflowAttention";
import { cn } from "@/lib/utils";

const relativeTime = (iso: string | null) => {
  if (!iso) return "";
  // Server timestamps are naive UTC
  const date = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(date.getTime()) ? "" : formatDistanceToNow(date, { addSuffix: true });
};

export function AttentionBell({ className }: { className?: string }) {
  const { notifications, muted, setMuted, openNotification, clearAll } = useWorkflowAttention();
  const [open, setOpen] = useState(false);
  const count = notifications.length;
  const needsInput = pendingInputCount(notifications) > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "relative grid h-9 w-9 place-items-center rounded-lg text-muted-foreground hover:bg-soft hover:text-foreground",
            className,
          )}
          aria-label={count ? `Notifications, ${count} pending` : "Notifications"}
        >
          <Bell className="h-[17px] w-[17px]" />
          {count > 0 && (
            <span
              className={cn(
                "absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] font-semibold leading-none",
                needsInput ? "bg-gradient-brand text-white" : "bg-muted-foreground text-background",
              )}
            >
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-input px-4 py-3">
          <strong className="text-sm font-medium text-foreground">Notifications</strong>
          <div className="flex items-center gap-1">
            {count > 0 && (
              <button
                className="rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-soft"
                onClick={clearAll}
              >
                Clear all
              </button>
            )}
            <button
              className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-soft hover:text-foreground"
              onClick={() => setMuted(!muted)}
              aria-pressed={muted}
            >
              {muted ? <BellOff className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
              {muted ? "Sound off" : "Sound on"}
            </button>
          </div>
        </div>
        {count === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            You're all caught up. We'll let you know when Joylo needs you.
          </p>
        ) : (
          <ul className="max-h-96 overflow-y-auto py-1">
            {notifications.map((n) => (
              <li key={n.eventKey}>
                <button
                  className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-soft"
                  onClick={() => {
                    setOpen(false);
                    openNotification(n);
                  }}
                >
                  <CircleDot
                    className={cn("mt-0.5 h-4 w-4 shrink-0", n.type === "input_required" ? "text-primary" : "text-muted-foreground")}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-medium text-foreground">{displayTitle(n)}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {attentionLine(n)}
                    </span>
                    <span className="block text-[11px] text-muted-foreground/80">{relativeTime(n.occurredAt)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
